import React, { useState, useEffect } from 'react';
import { Share2, RefreshCw, Copy, Check, Trash2 } from 'lucide-react';
import { API_BASE_URL } from '../../config';
import CreateLinkForm from './CreateLinkForm';

const ReferralsPage = () => {
    const [campaigns, setCampaigns] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const [newLinkData, setNewLinkData] = useState({ keyword: '' });
    const [linkError, setLinkError] = useState(null);
    const [generating, setGenerating] = useState(false);
    const [deletingId, setDeletingId] = useState(null);

    const loadCampaigns = async () => {
        try {
            setLoading(true);
            setError(null);
            const response = await fetch(`${API_BASE_URL}/api/referrals/campaigns`, {
                method: 'GET',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
            });
            if (!response.ok) {
                const errorText = await response.text();
                console.error('Failed to fetch campaigns:', response.status, errorText);
                throw new Error(`HTTP ${response.status}: ${errorText}`);
            }
            const data = await response.json();
            console.log('Campaigns loaded:', data);
            setCampaigns(data.campaigns || []);
        } catch (err) {
            console.error('Error loading campaigns:', err);
            setError(`Не удалось загрузить кампании: ${err.message}`);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadCampaigns();
    }, []);

    const handleCreateCampaign = async () => {
        const keyword = (newLinkData.keyword || '').trim();
        
        if (!keyword) {
            setLinkError('Укажите ключевое слово');
            return;
        }
        
        setLinkError(null);
        setGenerating(true);
        try {
            const r = await fetch(
                `${API_BASE_URL}/api/referrals/campaigns`,
                {
                    method: 'POST',
                    credentials: 'include',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ keyword })
                }
            );
            if (!r.ok) {
                const errorText = await r.text();
                let err;
                try {
                    err = JSON.parse(errorText);
                } catch {
                    err = { detail: errorText || 'Ошибка создания кампании' };
                }
                console.error('Failed to create campaign:', r.status, err);
                throw new Error(err.detail || 'Ошибка создания кампании');
            }
            setNewLinkData({ keyword: '' });
            await loadCampaigns();
        } catch (e) {
            setLinkError(e.message || 'Ошибка создания кампании');
        } finally {
            setGenerating(false);
        }
    };

    const handleDeleteCampaign = async (campaignId) => {
        if (!confirm('Вы уверены, что хотите удалить эту кампанию?')) {
            return;
        }
        setDeletingId(campaignId);
        try {
            const r = await fetch(
                `${API_BASE_URL}/api/referrals/campaigns/${campaignId}`,
                {
                    method: 'DELETE',
                    credentials: 'include',
                    headers: { 'Content-Type': 'application/json' }
                }
            );
            if (!r.ok) {
                const err = await r.json().catch(() => ({}));
                throw new Error(err.detail || 'Ошибка удаления кампании');
            }
            await loadCampaigns();
        } catch (e) {
            setError(e.message || 'Ошибка удаления кампании');
        } finally {
            setDeletingId(null);
        }
    };

    const generateLink = (keyword) => {
        return `https://t.me/Starspay9bot?start=${keyword}`;
    };

    const handleCopyLink = async (link) => {
        try {
            await navigator.clipboard.writeText(link);
        } catch (e) {
            setError('Не удалось скопировать');
        }
    };

    return (
        <div className="space-y-6">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                <h2 className="text-2xl font-bold text-slate-800">Реклама</h2>
                <button
                    onClick={loadCampaigns}
                    disabled={loading}
                    className="flex items-center space-x-2 bg-slate-800 text-white px-4 py-2 rounded-lg hover:bg-slate-700 transition disabled:opacity-50"
                >
                    <RefreshCw size={18} className={loading ? 'animate-spin' : ''} />
                    <span>Обновить</span>
                </button>
            </div>

            {/* Создание новой кампании */}
            <section className="space-y-4">
                <h3 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
                    <Share2 size={20} className="text-indigo-600" />
                    Рекламная система
                </h3>
                <CreateLinkForm
                    newLinkData={newLinkData}
                    setNewLinkData={setNewLinkData}
                    onCreate={handleCreateCampaign}
                    generating={generating}
                />
                {linkError && (
                    <div className="bg-rose-50 border border-rose-200 text-rose-700 px-4 py-3 rounded-lg text-sm">
                        {linkError}
                    </div>
                )}
            </section>

            {error && (
                <div className="bg-rose-50 border border-rose-200 text-rose-700 px-4 py-3 rounded-lg">
                    {error}
                </div>
            )}

            {/* Таблица кампаний */}
            <section>
                <h3 className="text-lg font-semibold text-slate-800 mb-3">Рекламные кампании</h3>
                {loading ? (
                    <div className="flex items-center justify-center py-12">
                        <RefreshCw className="animate-spin text-slate-400" size={32} />
                        <span className="ml-3 text-slate-600">Загрузка...</span>
                    </div>
                ) : (
                    <div className="bg-white rounded-xl shadow-sm border border-slate-100 overflow-hidden">
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-slate-200">
                                <thead className="bg-slate-50">
                                    <tr>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Ссылка</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Переходы</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Выручка</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Действия</th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-slate-200">
                                    {campaigns.map((campaign) => {
                                        const link = generateLink(campaign.keyword);
                                        return (
                                            <tr key={campaign.id} className="hover:bg-slate-50">
                                                <td className="px-6 py-4 text-sm text-slate-600">
                                                    <div className="flex items-center gap-2">
                                                        <code className="text-xs font-mono text-indigo-600 break-all max-w-xs">
                                                            {link}
                                                        </code>
                                                        <button
                                                            onClick={() => handleCopyLink(link)}
                                                            className="p-1 hover:bg-slate-100 rounded transition"
                                                            title="Копировать ссылку"
                                                        >
                                                            <Copy size={14} className="text-slate-400" />
                                                        </button>
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600">
                                                    {campaign.clicks || 0}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-green-600">
                                                    {parseFloat(campaign.revenue || 0).toLocaleString('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ₽
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm">
                                                    <button
                                                        onClick={() => handleDeleteCampaign(campaign.id)}
                                                        disabled={deletingId === campaign.id}
                                                        className="p-2 text-rose-600 hover:bg-rose-50 rounded transition disabled:opacity-50"
                                                        title="Удалить кампанию"
                                                    >
                                                        <Trash2 size={16} />
                                                    </button>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                        {campaigns.length === 0 && !loading && (
                            <div className="p-12 text-center text-slate-500">Кампаний пока нет. Создайте первую кампанию выше.</div>
                        )}
                    </div>
                )}
            </section>
        </div>
    );
};

export default ReferralsPage;
